import { useEffect, useMemo, useRef, useState } from 'react';
import { Heart, ChevronRight, HelpCircle, Crown, Gem } from 'lucide-react';
import PortfolioHealthScoreBadge from './PortfolioHealthScoreBadge';
import HealthScoreTrend from './HealthScoreTrend';
import usePriceStream from '@/hooks/usePriceStream';

/**
 * PortfolioHealthSnapshot — collapsed-by-default summary row that
 * sits below the "What these numbers mean for X" dropdown on every
 * portfolio section of the Income by Portfolio card.
 *
 * Round 95aa (Jun 2026) — first cut.
 * Round 95ab (Jun 2026) — UX rewrite per user feedback: a stranger
 * shouldn't have to ask what "5/5" means. Each bucket now has a
 * plain-English name above it (Strong / Solid / Mixed / Risky), a
 * `<title>` tooltip explaining what it represents, an intro
 * paragraph explaining the whole card, and an info popover linking
 * to the Triangle Buy Checklist.
 *
 * Clicking a weak-holding chip now lands the user directly on the
 * Triangle tab (forceTab='triangle') so they see the checklist
 * breakdown that flagged the position — much more useful than
 * dumping them on Dividend Info.
 */
const BUCKETS = [
  {
    key: 'fives',
    label: 'Strong',
    sub: '5/5',
    tickersKey: 'strong_tickers',
    title: 'Strong (passes all 5 Buy Checklist checks). High-conviction holdings.',
    tone: 'emerald',
  },
  {
    key: 'fours',
    label: 'Solid',
    sub: '4/5',
    tickersKey: 'solid_tickers',
    title: 'Solid (passes 4 of 5 checks). One area to watch but still a buy with a smaller position size.',
    tone: 'emerald',
  },
  {
    key: 'threes',
    label: 'Mixed',
    sub: '3/5',
    tickersKey: 'mixed_tickers',
    title: 'Mixed (passes 3 of 5 checks). Pause and investigate before adding more shares.',
    tone: 'amber',
  },
  {
    key: 'walk_aways',
    label: 'Risky',
    sub: '≤2/5',
    tickersKey: 'risky_tickers',
    title: 'Risky (passes 2 or fewer of 5 checks). Walk-away candidates — listed below for quick review.',
    tone: 'rose',
  },
];

export default function PortfolioHealthSnapshot({ snapshot, onStockClick, testIdPrefix, healthScoreInputs, hasPlusAccess = false, title = 'Conviction Snapshot', trend = null }) {
  const [helpOpen, setHelpOpen] = useState(false);
  // Jul 7, 2026 — Which long-streak chip is currently expanded.
  // Only one open at a time so the popover doesn't stack.
  // Values: null | 'multi-decade' | 'aristocrats'
  const [longStreakPopover, setLongStreakPopover] = useState(null);
  // Feb 2026 — Which Conviction bucket chip is currently expanded
  // (Strong / Solid / Mixed / Risky). Clicking a chip pushes it in,
  // shows the fade-out explainer above the grid, and reveals the
  // underlying stocks below. Only one bucket open at a time.
  const [activeBucket, setActiveBucket] = useState(null);   // 'fives' | 'fours' | 'threes' | 'walk_aways' | null
  const [explainerVisible, setExplainerVisible] = useState(false);
  const explainerTimerRef = useRef(null);
  useEffect(() => () => {
    if (explainerTimerRef.current) clearTimeout(explainerTimerRef.current);
  }, []);
  const toggleBucket = (key) => {
    setActiveBucket((cur) => {
      const next = cur === key ? null : key;
      // Restart the fade timer any time the explainer should show.
      if (explainerTimerRef.current) clearTimeout(explainerTimerRef.current);
      if (next) {
        setExplainerVisible(true);
        explainerTimerRef.current = setTimeout(() => setExplainerVisible(false), 3000);
      } else {
        setExplainerVisible(false);
      }
      return next;
    });
  };
  // Feb 2026 — Live prices for the Multi-Decade Payer detail rows.
  // Subscribe to the Massive WebSocket (15-min delayed) via the
  // existing SSE pipe so the "Price" column reflects the current
  // quote, not whatever stale snapshot stock_cache last persisted.
  // Guard: only pull the ticker list once the snapshot is known
  // AND at least one bucket is open — a page that never opens a
  // Conviction chip should not open an SSE.
  const mdTickers = useMemo(() => {
    if (!activeBucket) return [];
    const roster = snapshot?.multi_decade_tickers || [];
    return roster.map((r) => String(r.ticker || '').toUpperCase()).filter(Boolean);
  }, [activeBucket, snapshot]);
  const { prices: livePrices } = usePriceStream(mdTickers);
  if (!snapshot || !snapshot.total_analyzed) return null;

  const { total_analyzed, fives, fours, weak_holdings, tickers_total } = snapshot;
  // Round 95ag (Jun 2026) — Watchlist context surfaces strong
  // tickers (5/5 + 4/5) as "Buy for Portfolio" CTAs. The portfolio
  // variant of this card omits `strong_holdings` from the API
  // response since those tickers are already owned.
  const strongHoldings = Array.isArray(snapshot.strong_holdings) ? snapshot.strong_holdings : [];
  const strong = fives + fours;
  const strongPct = total_analyzed > 0 ? Math.round((strong / total_analyzed) * 100) : 0;

  let toneClass = 'border-amber-300/50 bg-amber-50/60 dark:bg-amber-950/30 dark:border-amber-800/40';
  let label = 'Mixed — some positions worth a closer look.';
  let labelTone = 'text-amber-900 dark:text-amber-200';
  const walkAways = snapshot.walk_aways;
  if (walkAways === 0 && fives >= total_analyzed * 0.5) {
    toneClass = 'border-emerald-300/50 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800/40';
    label = 'Solid footing — most holdings pass the checklist.';
    labelTone = 'text-emerald-900 dark:text-emerald-200';
  } else if (walkAways >= Math.max(1, total_analyzed * 0.34)) {
    toneClass = 'border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/40';
    label = 'Concentrated risk — review the walk-away positions below.';
    labelTone = 'text-rose-900 dark:text-rose-200';
  }

  // Trigger the parent's onStockClick with a forced tab. Dividend
  // payers land on 'triangle' (the Buy Checklist that explains WHY
  // the position is weak). Non-dividend tickers — flagged by the
  // backend via `is_non_dividend: true` — land on 'stock-metrics'
  // instead, because (a) the Triangle tab is dividend-only and (b)
  // not every non-div ticker has the Growth/Capital Analytics tabs
  // populated yet. Stock Metrics is the universal landing tab.
  const openOnExplainTab = (ticker, isNonDiv) => {
    if (!onStockClick) return;
    onStockClick(ticker, isNonDiv ? 'stock-metrics' : 'triangle');
  };

  // Round 95ag — "Buy for Portfolio" path: opens the Stock Modal on
  // the My Portfolio tab where the user records a purchase against
  // whichever portfolio they currently have selected. Avoids creating
  // a stub lot — user maintains full control.
  const openOnMyPortfolio = (ticker) => {
    if (!onStockClick) return;
    onStockClick(ticker, 'my-portfolio');
  };

  return (
    <div
      className={`mt-3 rounded-md border ${toneClass} px-3 py-2`}
      data-testid={`${testIdPrefix}-health-snapshot`}
    >
      {/* Header row — title + analyzed count + help toggle */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          {title && <Heart className="w-3.5 h-3.5 text-rose-500" />}
          {title && (
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground" data-testid={`${testIdPrefix}-health-snapshot-title`}>
              {title}
            </span>
          )}
          <button
            type="button"
            onClick={() => setHelpOpen((v) => !v)}
            className="p-0.5 rounded hover:bg-muted/40 transition-colors"
            title="What is this?"
            aria-label="What is this?"
            data-testid={`${testIdPrefix}-health-snapshot-help-toggle`}
          >
            <HelpCircle className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground" title={`${total_analyzed} of your ${tickers_total} positions had enough data to run through DiviDrip's Buy Checklist.`}>
          {total_analyzed}/{tickers_total} analyzed
        </span>
      </div>

      {/* Plain-English intro — shown by default. Toggles between
          short ("Solid footing — ...") and long (full help) on click. */}
      <p
        className={`text-xs font-semibold mb-2 ${labelTone}`}
        data-testid={`${testIdPrefix}-health-snapshot-label`}
      >
        {label}
      </p>

      {/* Round 96n (Jul 3 2026) — Portfolio Health Score. A single
          0–100 grade combining real yield, FCF coverage, revenue
          growth, concentration and walk-away exposure. Renders
          nothing (returns null internally) when fewer than 3 of the
          5 sub-scores can be computed. */}
      {healthScoreInputs && (
        <PortfolioHealthScoreBadge
          {...healthScoreInputs}
          walkAways={snapshot.walk_aways}
          totalAnalyzed={snapshot.total_analyzed}
          testIdPrefix={testIdPrefix}
        />
      )}
      {/* Sep 2026 — Health Score trend line (portfolio context only).
          `trend` = { portfolioId } — null portfolioId = all combined. */}
      {trend && <HealthScoreTrend portfolioId={trend.portfolioId ?? null} testIdPrefix={testIdPrefix} />}

      {/* Expandable help panel — only visible when user clicks the ? */}
      {helpOpen && (
        <div className="mb-2 rounded-md border border-border/60 bg-background/60 dark:bg-background/40 px-2.5 py-2 text-[11px] leading-snug text-foreground/90 space-y-1.5" data-testid={`${testIdPrefix}-health-snapshot-help`}>
          <p>
            <span className="font-bold">What is this?</span>{' '}
            DiviDrip runs every holding through a 5-point <span className="font-semibold">Buy Checklist</span>{' '}
            (FCF coverage, payout ratio, dividend streak, leverage, yield-in-sector — or asset-class-aware
            equivalents for REITs, BDCs and ETFs). The boxes below count how many of your positions land in each bucket.
          </p>
          <p>
            <span className="font-bold">How to read it:</span>{' '}
            Strong (5/5) + Solid (4/5) = compounding engines.
            Mixed (3/5) = investigate before adding shares.
            Risky (≤2/5) = walk-away candidates worth a review.
          </p>
          <p>
            <span className="font-bold">Click any risky chip below</span> to open that stock&apos;s Triangle tab and see exactly which checks it&apos;s failing.
          </p>
        </div>
      )}

      {/* The 4 buckets — each with its own plain-English label and
          a hover tooltip so a first-time user knows what they're
          looking at without reading the help panel.
          Feb 2026 — chips are now toggle buttons; clicking one
          "pushes it in", shows a 3-second fade-out explainer ABOVE
          the grid (so it doesn't cover the reveal below), and
          reveals the tickers in that bucket beneath the grid. */}
      <div className="relative">
        {activeBucket && explainerVisible && (
          <div
            className="absolute -top-1 left-0 right-0 -translate-y-full pb-1.5 z-10 pointer-events-none"
            data-testid={`${testIdPrefix}-health-snapshot-explainer`}
          >
            <div className="mx-auto max-w-md rounded-md border border-border/60 bg-background/95 dark:bg-background/85 backdrop-blur-sm px-2.5 py-1.5 shadow-md text-left text-[11px] leading-snug text-foreground/90 animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
              {BUCKETS.find((b) => b.key === activeBucket)?.title}
            </div>
          </div>
        )}
        <div className="grid grid-cols-4 gap-1.5 text-center mb-2">
          {BUCKETS.map((b) => (
            <Bucket
              key={b.key}
              count={snapshot[b.key] ?? 0}
              label={b.label}
              sub={b.sub}
              tone={b.tone}
              title={b.title}
              active={activeBucket === b.key}
              onToggle={() => toggleBucket(b.key)}
              testid={`${testIdPrefix}-health-snapshot-${b.key}`}
            />
          ))}
        </div>
      </div>

      {/* Feb 2026 — Reveal panel: pills for every ticker in the
          currently-active Conviction bucket. Multi-decade payers
          in the same bucket surface as the existing dropdown pill
          (rendered below via the shared longStreakPopover UI). All
          pills open the Stock Modal on click. */}
      {activeBucket && (() => {
        const bucketDef = BUCKETS.find((b) => b.key === activeBucket);
        const bucketTickers = snapshot[bucketDef?.tickersKey] || [];
        const mdMap = snapshot.multi_decade_by_ticker || {};
        const mdRoster = snapshot.multi_decade_tickers || [];
        const bucketMdRows = mdRoster.filter((r) => bucketTickers.includes(r.ticker));
        const pillTone = TONE_PILL[bucketDef?.tone] || TONE_PILL.emerald;
        return (
          <div
            className="-mt-1 mb-2 rounded-md border border-border/40 bg-background/60 dark:bg-background/40 px-2.5 py-2"
            data-testid={`${testIdPrefix}-health-snapshot-reveal`}
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground mb-1.5">
              {bucketDef?.label} positions{' '}
              <span className="font-mono">{bucketTickers.length}</span>
            </div>
            {bucketTickers.length === 0 ? (
              <p className="text-[11px] italic text-muted-foreground">No positions in this bucket.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-health-snapshot-reveal-pills`}>
                {bucketTickers.map((tkr) => (
                  <button
                    key={tkr}
                    type="button"
                    onClick={() => onStockClick && onStockClick(tkr)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold transition-colors cursor-pointer ${pillTone}`}
                    data-testid={`${testIdPrefix}-health-snapshot-reveal-pill-${tkr}`}
                    title={`Open the Stock Modal for ${tkr}`}
                  >
                    {tkr}
                    {mdMap[tkr] && (
                      <Gem className="w-2.5 h-2.5 opacity-70" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {/* Multi-decade payer sub-dropdown scoped to this
                bucket. Renders the existing pill (badge + count),
                and on expand shows the detailed stock-style rows
                (price · yield · ex-div · est payout) requested by
                the user. Whole row is clickable → Stock Modal. */}
            {bucketMdRows.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border/40">
                <button
                  type="button"
                  onClick={() => setLongStreakPopover(
                    longStreakPopover === 'multi-decade-bucket' ? null : 'multi-decade-bucket'
                  )}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all cursor-pointer ${
                    longStreakPopover === 'multi-decade-bucket'
                      ? 'bg-sky-500 text-white ring-2 ring-sky-400 shadow-md dark:bg-sky-600'
                      : 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200 ring-1 ring-sky-300/50 hover:bg-sky-200 dark:hover:bg-sky-900/60'
                  }`}
                  data-testid={`${testIdPrefix}-health-snapshot-reveal-md-toggle`}
                  title="Click to see detailed rows for the multi-decade payers in this bucket."
                >
                  <Gem className="w-3 h-3" />
                  {bucketMdRows.length} Multi-Decade Payer{bucketMdRows.length === 1 ? '' : 's'} in {bucketDef?.label}
                  <ChevronRight
                    className={`w-3 h-3 opacity-60 transition-transform ${longStreakPopover === 'multi-decade-bucket' ? 'rotate-90' : ''}`}
                  />
                </button>
                {longStreakPopover === 'multi-decade-bucket' && (
                  <div
                    className="mt-2 space-y-1"
                    data-testid={`${testIdPrefix}-health-snapshot-reveal-md-rows`}
                  >
                    {bucketMdRows.map((r) => (
                      <MultiDecadeRow
                        key={r.ticker}
                        row={r}
                        livePrice={livePrices?.[String(r.ticker).toUpperCase()]?.price}
                        onClick={() => onStockClick && onStockClick(r.ticker, 'dividend-info')}
                        testIdPrefix={`${testIdPrefix}-health-snapshot-reveal-md-row-${r.ticker}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Strong-positions strip — quick "X% on solid footing" read */}
      <div className="text-[10px] font-bold text-foreground/85 mb-1.5">
        <span className="font-bold">{strongPct}%</span> of analyzed positions land in Strong or Solid.
      </div>

      {/* Jul 7, 2026 — Multi-Decade Payers strip. Orthogonal to the
          checklist buckets — a position can be Solid (4/5) *and* an
          Aristocrat, or Strong (5/5) but only a 6-year payer. Now
          that the 15yr warmer accurately populates streak_years for
          non-curated tickers, this bucket is worth surfacing. Both
          counts inclusive: aristocrats_plus is a subset of
          multi_decade_payers, so the two chips read naturally as
          "12 total · 3 of which are Aristocrats+". Chips only
          render when the value is ≥1 to avoid meaningless "0"
          badges. */}
      {(snapshot.multi_decade_payers > 0 || snapshot.aristocrats_plus > 0) && (
        <div
          className="relative mb-2"
          data-testid={`${testIdPrefix}-health-snapshot-longstreak`}
        >
          <div className="flex flex-wrap gap-1.5">
            {snapshot.multi_decade_payers > 0 && (
              <button
                type="button"
                onClick={() => setLongStreakPopover(
                  longStreakPopover === 'multi-decade' ? null : 'multi-decade'
                )}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all cursor-pointer ${
                  longStreakPopover === 'multi-decade'
                    ? 'bg-sky-500 text-white ring-2 ring-sky-400 shadow-md dark:bg-sky-600'
                    : 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200 ring-1 ring-sky-300/50 hover:bg-sky-200 dark:hover:bg-sky-900/60'
                }`}
                title="Click to see which of your positions are Multi-Decade Payers."
                data-testid={`${testIdPrefix}-health-snapshot-multi-decade`}
              >
                <Gem className="w-3 h-3" />
                {snapshot.multi_decade_payers} Multi-Decade Payer{snapshot.multi_decade_payers === 1 ? '' : 's'}
                <ChevronRight
                  className={`w-3 h-3 opacity-60 transition-transform ${longStreakPopover === 'multi-decade' ? 'rotate-90' : ''}`}
                />
              </button>
            )}
            {snapshot.aristocrats_plus > 0 && (
              <button
                type="button"
                onClick={() => setLongStreakPopover(
                  longStreakPopover === 'aristocrats' ? null : 'aristocrats'
                )}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all cursor-pointer ${
                  longStreakPopover === 'aristocrats'
                    ? 'bg-amber-500 text-white ring-2 ring-amber-400 shadow-md dark:bg-amber-600'
                    : 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 ring-1 ring-amber-300/50 hover:bg-amber-200 dark:hover:bg-amber-900/60'
                }`}
                title="Click to see which of your positions are Aristocrats+ (25+ year streak)."
                data-testid={`${testIdPrefix}-health-snapshot-aristocrats-plus`}
              >
                <Crown className="w-3 h-3" />
                {snapshot.aristocrats_plus} Aristocrat{snapshot.aristocrats_plus === 1 ? '' : 's'}+
                <ChevronRight
                  className={`w-3 h-3 opacity-60 transition-transform ${longStreakPopover === 'aristocrats' ? 'rotate-90' : ''}`}
                />
              </button>
            )}
          </div>
          {longStreakPopover && (
            <div
              className="mt-2 rounded-md border border-border/60 bg-background/95 dark:bg-background/80 backdrop-blur-sm px-2.5 py-2 shadow-md"
              data-testid={`${testIdPrefix}-health-snapshot-longstreak-popover`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground mb-1.5">
                {longStreakPopover === 'multi-decade'
                  ? 'Multi-Decade Payers (≥10 yr streak) · longest first'
                  : 'Aristocrats+ (≥25 yr streak) · longest first'}
              </div>
              <div className="flex flex-wrap gap-1">
                {(longStreakPopover === 'multi-decade'
                  ? snapshot.multi_decade_tickers
                  : snapshot.aristocrats_tickers
                )?.map((t) => (
                  <button
                    key={t.ticker}
                    type="button"
                    onClick={() => onStockClick && onStockClick(t.ticker, 'dividend-info')}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-muted/60 hover:bg-muted text-foreground ring-1 ring-border/60 hover:ring-border transition-colors cursor-pointer"
                    title={`${t.ticker} — ${t.streak}-year dividend raise streak. Click to open the Stock Modal on the Dividend Info tab.`}
                    data-testid={`${testIdPrefix}-health-snapshot-longstreak-ticker-${t.ticker}`}
                  >
                    {t.ticker}
                    <span className="text-muted-foreground font-normal">{t.streak}y</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Round 95ag (Jun 2026) — Strong-holdings "Buy for Portfolio"
          chip cluster. Renders only when the snapshot exposes
          `strong_holdings` (watchlist context, not portfolio). One
          click jumps to the Stock Modal's My Portfolio tab where the
          user records a purchase against their currently-selected
          portfolio — no auto-stub lot creation. Sits above the
          walk-away cluster so the positive CTA wins eye-track first. */}
      {strongHoldings.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-border/40">
          <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground mb-1 flex items-center gap-1">
            Ready to buy
            <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
              {'·'} click to record a purchase
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-health-snapshot-strong`}>
            {strongHoldings.map((s) => (
              <button
                key={s.ticker}
                type="button"
                onClick={() => openOnMyPortfolio(s.ticker)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 ring-1 ring-emerald-300/50 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors cursor-pointer"
                data-testid={`${testIdPrefix}-health-snapshot-strong-${s.ticker}`}
                title={`Buy ${s.ticker} for your current portfolio — passes ${s.passing}/${s.total} Buy Checklist checks. Opens the Stock Modal on the My Portfolio tab where you can record a purchase.`}
              >
                {s.ticker}
                <span className="text-emerald-700/80 dark:text-emerald-300/80 font-mono">{s.passing}/{s.total}</span>
                <ChevronRight className="w-3 h-3 opacity-60" />
              </button>
            ))}
          </div>
          <p
            className="mt-1.5 mb-2 text-[10px] font-semibold text-amber-600 dark:text-amber-400"
            data-testid={`${testIdPrefix}-health-snapshot-strong-cta-note`}
          >
            Click to open the Stock Modal to the &ldquo;My Portfolio&rdquo; Tab, to record a purchase.
          </p>
        </div>
      )}

      {/* Drill-down: weakest 3 holdings (≤2/5), ranked by income weight */}
      {Array.isArray(weak_holdings) && weak_holdings.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-border/40">
          <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground mb-1 flex items-center gap-1">
            Walk-away positions to review
            <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
              {'·'} click to see the failing checks
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-health-snapshot-weak`}>
            {weak_holdings.map((w) => (
              <button
                key={w.ticker}
                type="button"
                onClick={() => openOnExplainTab(w.ticker, w.is_non_dividend)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 ring-1 ring-rose-300/50 hover:bg-rose-200 dark:hover:bg-rose-900/60 transition-colors cursor-pointer"
                data-testid={`${testIdPrefix}-health-snapshot-weak-${w.ticker}`}
                title={
                  w.is_non_dividend
                    ? `Open ${w.ticker} on the Stock Metrics tab — ${w.passing}/${w.total} on the Non-Dividend Quality scale (SPECULATIVE-tier walk-away).`
                    : w.income_weight_pct != null
                    ? `Open ${w.ticker} on the Triangle tab — passes ${w.passing}/${w.total} Buy Checklist checks (${w.income_weight_pct}% of this portfolio's income).`
                    : `Open ${w.ticker} on the Triangle tab — passes ${w.passing}/${w.total} Buy Checklist checks.`
                }
              >
                {w.ticker}
                <span className="text-rose-600/80 dark:text-rose-300/80 font-mono">{w.passing}/{w.total}</span>
                <ChevronRight className="w-3 h-3 opacity-60" />
              </button>
            ))}
          </div>
          <p
            className="mt-1.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400"
            data-testid={`${testIdPrefix}-health-snapshot-weak-cta-note`}
          >
            {hasPlusAccess
              ? 'Click to open the Stock Modal to the either the "Triangle" Tab, or the "Stock Metrics" Tab, for each Dividend or Non-Dividend Position.'
              : 'Click to open the Stock Modal to the "Triangle" Tab, for each Dividend Position.'}
          </p>
        </div>
      )}
    </div>
  );
}

function Bucket({ count, label, sub, tone, title, testid, active, onToggle }) {
  const STYLES = {
    emerald: count > 0
      ? 'border-emerald-300/60 bg-emerald-100/60 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
      : 'border-border bg-muted/40 text-muted-foreground',
    amber: count > 0
      ? 'border-amber-300/60 bg-amber-100/60 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
      : 'border-border bg-muted/40 text-muted-foreground',
    rose: count > 0
      ? 'border-rose-300/60 bg-rose-100/60 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200'
      : 'border-border bg-muted/40 text-muted-foreground',
  };
  const ACTIVE_STYLES = {
    emerald: 'border-emerald-500 bg-emerald-200/70 dark:bg-emerald-800/60 text-emerald-900 dark:text-emerald-100 ring-2 ring-emerald-400/60 shadow-inner',
    amber:   'border-amber-500 bg-amber-200/70 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100 ring-2 ring-amber-400/60 shadow-inner',
    rose:    'border-rose-500 bg-rose-200/70 dark:bg-rose-800/60 text-rose-900 dark:text-rose-100 ring-2 ring-rose-400/60 shadow-inner',
  };
  const styleClass = active
    ? (ACTIVE_STYLES[tone] || ACTIVE_STYLES.emerald)
    : (STYLES[tone] || '');
  // Feb 2026 — chips are proper toggle buttons so any position count
  // (even 0) is inspectable. The pushed-in ring + inset shadow gives
  // the "pushed in" state the user requested; scale-95 adds a subtle
  // press micro-animation for tactile feedback.
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-md border px-1.5 py-1 w-full text-center transition-all cursor-pointer hover:brightness-105 ${styleClass} ${active ? 'scale-[0.97]' : ''}`}
      title={title}
      aria-pressed={active}
      data-testid={testid}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-90 leading-tight">{label}</div>
      <div className="text-lg font-bold font-mono leading-tight my-0.5">{count}</div>
      <div className="text-[9px] font-bold font-mono leading-tight">{sub}</div>
    </button>
  );
}

// Feb 2026 — pill tone lookup used by the bucket-reveal ticker
// pills. Mirrors the Bucket palette so the pills clearly belong to
// the chip that revealed them.
const TONE_PILL = {
  emerald: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 ring-1 ring-emerald-300/50 hover:bg-emerald-200 dark:hover:bg-emerald-900/60',
  amber:   'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 ring-1 ring-amber-300/50 hover:bg-amber-200 dark:hover:bg-amber-900/60',
  rose:    'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 ring-1 ring-rose-300/50 hover:bg-rose-200 dark:hover:bg-rose-900/60',
};

// Feb 2026 — Detail row for a multi-decade payer surfaced via the
// bucket reveal. Whole row is a button → Stock Modal on the Dividend
// Info tab so the user can drill into the streak, ex-div, and payout
// history. Fields are best-effort: any missing value renders "—" so
// the row height stays consistent.
function MultiDecadeRow({ row, onClick, testIdPrefix, livePrice }) {
  // Feb 2026 — live-price-first display. `livePrice` streams in from
  // the Massive WebSocket (15-min delayed) via usePriceStream; fall
  // back to the stock_cache snapshot only when the stream hasn't
  // delivered a tick yet. Once the tick arrives, the row snaps to
  // the fresh number without an extra render pass.
  const priceToShow = (livePrice != null && Number.isFinite(Number(livePrice)))
    ? Number(livePrice)
    : (row.price != null && Number.isFinite(Number(row.price)) ? Number(row.price) : null);
  // Feb 2026 — Live-yield recompute. Yield% = annual dividend $ / price.
  // Recompute against whichever price we ACTUALLY show (live tick
  // preferred, stock_cache fallback) so the Yield and Price columns
  // are always internally consistent. Falls back to the pre-computed
  // `yield_pct` from stock_cache only when annual_dividend is missing
  // (never-warmed ticker), so the row is never blank.
  const liveYieldPct = (
    priceToShow != null && priceToShow > 0 &&
    row.annual_dividend != null && Number.isFinite(Number(row.annual_dividend))
  )
    ? (Number(row.annual_dividend) / priceToShow) * 100
    : null;
  const fmtPrice = (v) => (v == null ? '—' : `$${v.toFixed(2)}`);
  const fmtPct = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return '—';
    const n = Number(v);
    // Backend stores dividend_yield as fraction OR percent depending
    // on source. Anything ≤1 is treated as a fraction; otherwise it's
    // already a percent value (e.g. 3.42 → 3.42%).
    return `${(n <= 1 ? n * 100 : n).toFixed(2)}%`;
  };
  const yieldDisplay = liveYieldPct != null ? `${liveYieldPct.toFixed(2)}%` : fmtPct(row.yield_pct);
  const fmtDate = (v) => {
    if (!v) return '—';
    // Robust across the two shapes the backend can hand us:
    //   • ISO string  "2026-08-26" / "2026-08-26T00:00:00"
    //   • Serialized  "Aug 26, 2026" from an upstream formatter
    // Parse into a Date and reformat as "MMM DD, YYYY" so the row is
    // consistent no matter which source populated it.
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) {
      // Not a parseable date — return the raw string trimmed.
      return String(v).trim() || '—';
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };
  const fmtPayout = (v) => (v == null || !Number.isFinite(Number(v))) ? '—' : `$${Number(v).toFixed(4)}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 text-left rounded-md border border-sky-200/60 dark:border-sky-800/40 bg-sky-50/50 dark:bg-sky-950/20 hover:bg-sky-100/70 dark:hover:bg-sky-900/40 px-2.5 py-1.5 transition-colors cursor-pointer"
      data-testid={testIdPrefix}
      title={`Open the Stock Modal for ${row.ticker} on the Dividend Info tab.`}
    >
      <span className="font-mono font-bold text-[12px] text-foreground shrink-0 w-14 -translate-y-px">{row.ticker}</span>
      <span className="text-[10px] font-mono text-sky-700 dark:text-sky-300 shrink-0 w-10 -translate-y-px" title="Consecutive-raise streak">
        {row.streak}y
      </span>
      <span className="grid grid-cols-4 gap-2 flex-1 text-[10px] font-mono -translate-y-px">
        <span className="text-muted-foreground">
          <span className="block text-[9px] uppercase tracking-wider opacity-70">Price</span>
          <span
            className={`text-foreground ${livePrice != null ? 'transition-colors duration-300' : ''}`}
            title={livePrice != null ? 'Live price — Massive WebSocket (15-min delayed)' : undefined}
          >{fmtPrice(priceToShow)}</span>
        </span>
        <span className="text-muted-foreground">
          <span className="block text-[9px] uppercase tracking-wider opacity-70">Yield</span>
          <span
            className={`text-foreground ${liveYieldPct != null ? 'transition-colors duration-300' : ''}`}
            title={
              row.annual_dividend != null && Number.isFinite(Number(row.annual_dividend))
                ? (liveYieldPct != null
                    ? `Live yield — annual dividend $${Number(row.annual_dividend).toFixed(2)} ÷ live price`
                    : `Annual dividend $${Number(row.annual_dividend).toFixed(2)} · yield from stock cache (awaiting live tick)`)
                : undefined
            }
          >{yieldDisplay}</span>
        </span>
        <span className="text-muted-foreground">
          <span className="block text-[9px] uppercase tracking-wider opacity-70">Ex-Div</span>
          <span className="text-foreground">{fmtDate(row.ex_date)}</span>
        </span>
        <span className="text-muted-foreground">
          <span className="block text-[9px] uppercase tracking-wider opacity-70">Est. Payout</span>
          <span className="text-foreground">{fmtPayout(row.est_payout)}</span>
        </span>
      </span>
      <ChevronRight className="w-3.5 h-3.5 text-sky-500/80 shrink-0 -translate-y-px" />
    </button>
  );
}
